import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, Globe, Tag } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { contactsService } from 'api/services/contacts.service';
import { WhatsAppTemplate, Contact } from 'types';

interface CampaignCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CampaignCreateModal: React.FC<CampaignCreateModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    title: '',
    template_uid: '',
    contact_group: 'all',
    contact_labels: [] as string[],
    schedule_at: '',
    expire_at: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchContacts();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    // Dedicated approved endpoint returns ALL approved templates. Previously this
    // called getTemplates() (paginated, size 20) then filtered APPROVED client-side,
    // so it saw at most 20 total templates before filtering — often far fewer than
    // the business's approved set.
    const result = await whatsappService.getApprovedTemplates();
    if (result.success && result.data) {
      setTemplates(Array.isArray(result.data) ? result.data : []);
    }
  };

  const fetchContacts = async () => {
    const result = await contactsService.getContacts({ channel: 'whatsapp' });
    if (result.success && result.data) {
      setContacts(result.data.data);
    }
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Campaign title is required';
    }

    if (!formData.template_uid) {
      newErrors.template_uid = 'Please select a template';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contact_group) {
      newErrors.contact_group = 'Please select a contact group';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    // Transform form data to match CreateCampaignRequest interface
    const campaignData = {
      name: formData.title,
      templateId: formData.template_uid,
      audienceType: formData.contact_group,
      audienceFilter: formData.contact_group === 'labels'
        ? formData.contact_labels.join(',')
        : undefined,
      scheduledAt: formData.schedule_at
        ? new Date(formData.schedule_at).getTime()
        : undefined,
    };

    const result = await whatsappService.createCampaign(campaignData);

    if (result.success) {
      alert('Campaign created successfully!');
      onSuccess();
      handleClose();
    } else {
      alert(result.error || 'Failed to create campaign');
    }

    setLoading(false);
  };

  const handleClose = () => {
    setFormData({
      title: '',
      template_uid: '',
      contact_group: 'all',
      contact_labels: [],
      schedule_at: '',
      expire_at: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setErrors({});
    setStep(1);
    onClose();
  };

  const toggleLabel = (label: string) => {
    setFormData(prev => ({
      ...prev,
      contact_labels: prev.contact_labels.includes(label)
        ? prev.contact_labels.filter(l => l !== label)
        : [...prev.contact_labels, label],
    }));
  };

  // Get unique labels from contacts
  const availableLabels = Array.from(new Set(contacts.flatMap(c => c.labels || [])));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Campaign</h2>
            <p className="text-sm text-gray-600 mt-1">Step {step} of 3</p>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
        <div className="overflow-y-auto flex-1">
        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                    s <= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      s < step ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
          {/* Step 1: Campaign Details & Template */}
          {step === 1 && (
            <div className="px-6 pb-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Campaign Details</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Summer Sale 2024"
                />
                {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Template *
                </label>
                <select
                  value={formData.template_uid}
                  onChange={(e) => setFormData({ ...formData, template_uid: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.template_uid ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Choose a template...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.language.toUpperCase()}) - {template.category}
                    </option>
                  ))}
                </select>
                {errors.template_uid && <p className="text-xs text-red-600 mt-1">{errors.template_uid}</p>}
              </div>

              {formData.template_uid && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Selected:</strong>{' '}
                    {templates.find(t => t.id === formData.template_uid)?.name}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Audience Selection */}
          {step === 2 && (
            <div className="px-6 pb-6 space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select Audience
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Group *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="contact_group"
                      value="all"
                      checked={formData.contact_group === 'all'}
                      onChange={(e) => setFormData({ ...formData, contact_group: e.target.value })}
                      className="text-blue-600"
                    />
                    <div>
                      <p className="font-medium">All Contacts</p>
                      <p className="text-sm text-gray-600">{contacts.length} contacts</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="contact_group"
                      value="labels"
                      checked={formData.contact_group === 'labels'}
                      onChange={(e) => setFormData({ ...formData, contact_group: e.target.value })}
                      className="text-blue-600"
                    />
                    <div>
                      <p className="font-medium">By Labels</p>
                      <p className="text-sm text-gray-600">Select specific labels</p>
                    </div>
                  </label>
                </div>
              </div>

              {formData.contact_group === 'labels' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Labels
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableLabels.length > 0 ? (
                      availableLabels.map((label) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => toggleLabel(label)}
                          className={`px-3 py-1.5 rounded-lg border transition ${
                            formData.contact_labels.includes(label)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                          }`}
                        >
                          <Tag className="w-3 h-3 inline mr-1" />
                          {label}
                        </button>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No labels available</p>
                    )}
                  </div>
                  {formData.contact_labels.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {contacts.filter(c => formData.contact_labels.some(label => c.labels?.includes(label))).length} contacts selected
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Scheduling */}
          {step === 3 && (
            <div className="px-6 pb-6 space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Schedule Campaign
              </h3>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  Leave schedule time empty to send immediately
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule At (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.schedule_at}
                  onChange={(e) => setFormData({ ...formData, schedule_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expire At (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.expire_at}
                  onChange={(e) => setFormData({ ...formData, expire_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Timezone
                </label>
                <input
                  type="text"
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., America/New_York"
                />
              </div>

              {/* Summary */}
              <div className="border-t pt-4 mt-6">
                <h4 className="font-medium text-gray-900 mb-3">Campaign Summary</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Title:</dt>
                    <dd className="font-medium">{formData.title}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Template:</dt>
                    <dd className="font-medium">
                      {templates.find(t => t.id === formData.template_uid)?.name}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Recipients:</dt>
                    <dd className="font-medium">
                      {formData.contact_group === 'all'
                        ? `All contacts (${contacts.length})`
                        : `${contacts.filter(c => formData.contact_labels.some(label => c.labels?.includes(label))).length} contacts`}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Schedule:</dt>
                    <dd className="font-medium">
                      {formData.schedule_at
                        ? new Date(formData.schedule_at).toLocaleString()
                        : 'Send immediately'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </div>

          {/* Footer Buttons */}
          <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50 flex-shrink-0">
            <button
              type="button"
              onClick={step === 1 ? handleClose : handleBack}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            <div className="flex gap-3">
              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="tl-btn tl-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Creating...' : 'Create Campaign'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignCreateModal;
